import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { z } from 'zod';

import Modal from '@/components/Modal';
import { useAuthContext } from '@/components/Providers/AuthProvider';
import { useGetPetVerticalListQuery, useUpdateCustomerPetVerticalMutation } from '@/store/api/account';
import { IPetVertical } from '@/types/Customer';

import Button from '../../Button';
import Spinner from '../../Spinner';
import CustomSelect from '../../Widgets/CustomSelect';

const petVerticalSchema = z.object({
  petVertical: z.string().trim().min(1, 'Please Select an Option'),
});

const PetVerticalModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [petVertical, setPetVertical] = useState('');
  const [petVerticalError, setPetVerticalError] = useState('');
  const { customer, isLoading: isCustomerLoading } = useAuthContext();
  const { data: petVerticalList, isLoading: isPetVerticalListLoading } = useGetPetVerticalListQuery();

  const [updateCustomerPetVertical, { isLoading }] = useUpdateCustomerPetVerticalMutation();

  useEffect(() => {
    if (
      !customer?.petVertical &&
      isPetVerticalListLoading === false &&
      isCustomerLoading === false &&
      petVerticalList &&
      petVerticalList.data.length
    ) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [customer?.petVertical, isPetVerticalListLoading, petVerticalList, isCustomerLoading]);

  const onSaveChanges = async () => {
    const validationResult = petVerticalSchema.safeParse({ petVertical });

    if (!validationResult.success) {
      const errorMessage = validationResult.error.issues[0]?.message || 'Please Select an Option';
      setPetVerticalError(errorMessage);
      return;
    }

    setPetVerticalError('');

    try {
      const res = await updateCustomerPetVertical({
        petVertical: petVertical,
      }).unwrap();
      if (!res.success) {
        toast.error(res.message || res.exceptionMessage);
      } else {
        toast.success(res.message || 'Pet vertical updated successfully');
        setIsOpen(false);
      }
    } catch (error: unknown) {
      const err = error as {
        data?: { title?: string; message?: string; exceptionMessage?: string };
      };
      toast.error(
        err.data?.title || err.data?.message || err.data?.exceptionMessage || 'Failed to update pet vertical'
      );
    }
  };

  const petVerticalOptions = (petVerticalList?.data?.map((item: IPetVertical) => ({
    name: item.value,
    value: item.key,
  })) || []) as { name: string; value: string }[];

  const onClose = () => {
    setIsOpen(false);
    setPetVerticalError('');
  };

  return (
    <Modal
      titleWrapperClassName="v2-px-6 v2-pt-6 !v2-mb-4"
      isOpen={isOpen}
      onClose={onClose}
      title="Welcome Back!"
      className="v2-max-w-[592px] !v2-p-0"
      closeBtnContainerClassName="v2-top-6 v2-right-6"
    >
      <div className="v2-px-6 v2-pb-6">
        <p className="v2-text-sm v2-leading-[18px] v2-text-[#121212] v2-font-normal v2-font-manrope-normal v2-mb-4">
          To ensure we provide you with the most helpful communication and recognition, please select the option that
          best describes you.
        </p>
        <CustomSelect
          variant="boxed"
          displayKey="name"
          valueKey="value"
          value={petVertical}
          labelClassName="!v2-font-manrope v2-font-normal v2-text-sm v2-text-[#344054]"
          className="!v2-h-11 v2-font-manrope-light v2-font-light v2-text-base v2-text-[#667085]"
          options={petVerticalOptions}
          placeholderClassName="v2-text-[#777777] v2-text-sm v2-font-manrope v2-font-normal"
          onChange={value => {
            setPetVertical(value);
            if (petVerticalError) {
              setPetVerticalError('');
            }
          }}
        />
        {petVerticalError ? <p className="v2-mt-2 v2-text-xs v2-text-[#D92D20]">{petVerticalError}</p> : null}
      </div>
      <div className="v2-flex v2-justify-between v2-items-center v2-gap-3 v2-border-t v2-border-[#EAECF0] v2-pt-5 v2-px-6 v2-pb-6">
        <Button
          btnSize="md"
          as="button"
          type="button"
          scheme="secondary"
          variant="outlined"
          className="v2-h-11 v2-w-full"
          onClick={onClose}
        >
          Close
        </Button>
        <Button
          btnSize="md"
          as="button"
          type="button"
          scheme="primary"
          variant="filled"
          className="v2-h-11 v2-w-full v2-flex v2-items-center v2-justify-center v2-gap-2"
          onClick={onSaveChanges}
          disabled={isLoading}
        >
          {isLoading ? (
            <div className="v2-flex v2-items-center v2-justify-center v2-gap-2">
              <Spinner /> Saving
            </div>
          ) : (
            'Save Changes'
          )}
        </Button>
      </div>
    </Modal>
  );
};

export default PetVerticalModal;
